import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { customerAPI, cityAPI } from "../../utils/api";
import { useNavigate } from "react-router-dom";
import {
  Package,
  MapPin,
  DollarSign,
  Truck,
  Weight,
  Ruler,
  CreditCard,
  FileText,
  ChevronRight,
  CheckCircle,
  Loader2,
  AlertCircle,
  Home,
  Phone,
  User,
  ArrowLeft,
} from "lucide-react";
import toast from "react-hot-toast";

const packageTypes = [
  {
    id: "document",
    label: "Document",
    icon: FileText,
    description: "Letters, contracts, papers",
  },
  {
    id: "parcel",
    label: "Parcel",
    icon: Package,
    description: "Boxes, small packages",
  },
  { id: "food", label: "Food", icon: Truck, description: "Meals, groceries" },
  {
    id: "fragile",
    label: "Fragile",
    icon: AlertCircle,
    description: "Glass, electronics",
  },
  { id: "other", label: "Other", icon: Package, description: "Special items" },
];

const deliveryTypes = [
  {
    id: "standard",
    label: "Standard",
    price: 25,
    time: "2-3 days",
    icon: Truck,
  },
  { id: "express", label: "Express", price: 45, time: "Next day", icon: Truck },
  {
    id: "same_day",
    label: "Same Day",
    price: 75,
    time: "Within 6 hours",
    icon: Truck,
  },
];

const paymentMethods = [
  { id: "cash", label: "Cash on Delivery", icon: DollarSign },
  { id: "card", label: "Credit Card", icon: CreditCard },
  { id: "online", label: "Online Payment", icon: CreditCard },
];

const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center mb-8">
    {steps.map((step, index) => (
      <div key={step.id} className="flex items-center">
        <div
          className={`flex flex-col items-center ${index <= currentStep ? "text-primary-600" : "text-gray-400"}`}
        >
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
              index < currentStep
                ? "bg-primary-600 border-primary-600 text-white"
                : index === currentStep
                  ? "border-primary-600 text-primary-600"
                  : "border-gray-300 text-gray-400"
            }`}
          >
            {index < currentStep ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <span className="font-semibold text-sm">{index + 1}</span>
            )}
          </div>
          <span className="text-xs font-medium mt-1.5">{step.label}</span>
        </div>
        {index < steps.length - 1 && (
          <div
            className={`w-16 h-0.5 mx-2 transition-all ${index < currentStep ? "bg-primary-600" : "bg-gray-300"}`}
          />
        )}
      </div>
    ))}
  </div>
);

const CustomerNewOrder = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [order, setOrder] = useState({
    pickup_address: "",
    pickup_city_id: "",
    delivery_address: "",
    delivery_city_id: "",
    recipient_name: "",
    recipient_phone: "",
    package_type: "parcel",
    weight: 1,
    dimensions: "",
    notes: "",
    delivery_type: "standard",
    payment_method: "cash",
  });
  const [errors, setErrors] = useState({});
  const [calculatedPrice, setCalculatedPrice] = useState(null);

  // Fetch cities
  const { data: citiesData, isLoading: citiesLoading } = useQuery({
    queryKey: ["cities"],
    queryFn: () => cityAPI.list({ per_page: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  const cities = citiesData?.data?.data?.data || [];

  // Calculate price when cities/weight/delivery type change
  useEffect(() => {
    const calculatePrice = async () => {
      if (order.pickup_city_id && order.delivery_city_id && order.weight > 0) {
        try {
          const res = await customerAPI.calculatePrice({
            pickup_city_id: parseInt(order.pickup_city_id),
            delivery_city_id: parseInt(order.delivery_city_id),
            weight: parseFloat(order.weight),
            delivery_type: order.delivery_type,
          });
          setCalculatedPrice(res.data?.data);
        } catch (e) {
          // Silent fail - use default pricing
          console.error("Price calculation failed:", e);
        }
      }
    };
    calculatePrice();
  }, [
    order.pickup_city_id,
    order.delivery_city_id,
    order.weight,
    order.delivery_type,
  ]);

  const steps = [
    { id: "locations", label: "Locations" },
    { id: "package", label: "Package" },
    { id: "delivery", label: "Delivery" },
    { id: "confirm", label: "Confirm" },
  ];

  const createMutation = useMutation({
    mutationFn: (data) => customerAPI.createOrder(data),
    onSuccess: () => {
      toast.success("Order created successfully!");
      navigate("/orders");
    },
    onError: (error) => {
      const serverErrors = error.response?.data?.errors;
      if (serverErrors) {
        setErrors(serverErrors);
        toast.error("Please fix the validation errors");
      } else {
        toast.error(error.response?.data?.message || "Failed to create order");
      }
    },
  });

  const validateStep = () => {
    const newErrors = {};
    if (step === 0) {
      if (!order.pickup_address.trim())
        newErrors.pickup_address = "Pickup address is required";
      if (!order.pickup_city_id)
        newErrors.pickup_city_id = "Pickup city is required";
      if (!order.delivery_address.trim())
        newErrors.delivery_address = "Delivery address is required";
      if (!order.delivery_city_id)
        newErrors.delivery_city_id = "Delivery city is required";
      if (!order.recipient_name.trim())
        newErrors.recipient_name = "Recipient name is required";
      if (!order.recipient_phone.trim())
        newErrors.recipient_phone = "Recipient phone is required";
    }
    if (step === 1) {
      if (!order.weight || order.weight <= 0)
        newErrors.weight = "Weight must be greater than 0";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = () => {
    const orderData = {
      ...order,
      pickup_city_id: parseInt(order.pickup_city_id),
      delivery_city_id: parseInt(order.delivery_city_id),
      weight: parseFloat(order.weight),
      distance_km: calculatedPrice?.distance_km || 10,
      subtotal: calculatedPrice?.subtotal || 0,
      delivery_fee:
        calculatedPrice?.base_fee + calculatedPrice?.distance_fee || 0,
      tax: calculatedPrice?.tax || 0,
      total_amount: calculatedPrice?.total || 0,
    };
    createMutation.mutate(orderData);
  };

  const updateField = (field, value) => {
    setOrder((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const getCityName = (cityId) => {
    const city = cities.find((c) => c.id == cityId);
    return city ? `${city.name}, ${city.governorate}` : "";
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Order</h1>
          <p className="text-gray-500 mt-1">Create a new delivery order</p>
        </div>
      </div>

      <StepIndicator currentStep={step} steps={steps} />

      {/* Step 1: Locations */}
      {step === 0 && (
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Home className="w-5 h-5 mr-2 text-amber-500" />
              Pickup Location
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pickup City <span className="text-red-500">*</span>
                </label>
                <select
                  value={order.pickup_city_id}
                  onChange={(e) =>
                    updateField("pickup_city_id", e.target.value)
                  }
                  className={`input-field w-full ${errors.pickup_city_id ? "border-red-500" : ""}`}
                  disabled={citiesLoading}
                >
                  <option value="">
                    {citiesLoading ? "Loading cities..." : "Select city..."}
                  </option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}, {city.governorate}
                    </option>
                  ))}
                </select>
                {errors.pickup_city_id && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.pickup_city_id}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pickup Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={order.pickup_address}
                  onChange={(e) =>
                    updateField("pickup_address", e.target.value)
                  }
                  className={`input-field w-full ${errors.pickup_address ? "border-red-500" : ""}`}
                  rows={2}
                  placeholder="Enter full pickup address..."
                />
                {errors.pickup_address && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.pickup_address}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-emerald-500" />
              Delivery Location
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery City <span className="text-red-500">*</span>
                </label>
                <select
                  value={order.delivery_city_id}
                  onChange={(e) =>
                    updateField("delivery_city_id", e.target.value)
                  }
                  className={`input-field w-full ${errors.delivery_city_id ? "border-red-500" : ""}`}
                  disabled={citiesLoading}
                >
                  <option value="">
                    {citiesLoading ? "Loading cities..." : "Select city..."}
                  </option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}, {city.governorate}
                    </option>
                  ))}
                </select>
                {errors.delivery_city_id && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.delivery_city_id}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={order.delivery_address}
                  onChange={(e) =>
                    updateField("delivery_address", e.target.value)
                  }
                  className={`input-field w-full ${errors.delivery_address ? "border-red-500" : ""}`}
                  rows={2}
                  placeholder="Enter full delivery address..."
                />
                {errors.delivery_address && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.delivery_address}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={order.recipient_name}
                      onChange={(e) =>
                        updateField("recipient_name", e.target.value)
                      }
                      className={`input-field pl-10 w-full ${errors.recipient_name ? "border-red-500" : ""}`}
                      placeholder="John Doe"
                    />
                  </div>
                  {errors.recipient_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.recipient_name}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient Phone <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={order.recipient_phone}
                      onChange={(e) =>
                        updateField("recipient_phone", e.target.value)
                      }
                      className={`input-field pl-10 w-full ${errors.recipient_phone ? "border-red-500" : ""}`}
                      placeholder="+20 1XX XXX XXXX"
                    />
                  </div>
                  {errors.recipient_phone && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.recipient_phone}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Package */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Package className="w-5 h-5 mr-2 text-violet-500" />
              Package Details
            </h3>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Package Type
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {packageTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => updateField("package_type", type.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          order.package_type === type.id
                            ? "border-primary-500 bg-primary-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <Icon
                          className={`w-6 h-6 mb-2 ${order.package_type === type.id ? "text-primary-600" : "text-gray-400"}`}
                        />
                        <p
                          className={`font-semibold text-sm ${order.package_type === type.id ? "text-primary-900" : "text-gray-900"}`}
                        >
                          {type.label}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {type.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weight (kg) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Weight className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={order.weight}
                      onChange={(e) =>
                        updateField("weight", parseFloat(e.target.value) || 0)
                      }
                      className={`input-field pl-10 w-full ${errors.weight ? "border-red-500" : ""}`}
                    />
                  </div>
                  {errors.weight && (
                    <p className="text-red-500 text-xs mt-1">{errors.weight}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dimensions
                  </label>
                  <div className="relative">
                    <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={order.dimensions}
                      onChange={(e) =>
                        updateField("dimensions", e.target.value)
                      }
                      className="input-field pl-10 w-full"
                      placeholder="30x20x15 cm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={order.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  className="input-field w-full"
                  rows={3}
                  placeholder="Special instructions, fragile, etc."
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Delivery */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Truck className="w-5 h-5 mr-2 text-blue-500" />
              Delivery Options
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Delivery Speed
                </label>
                <div className="space-y-3">
                  {deliveryTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => updateField("delivery_type", type.id)}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center ${
                          order.delivery_type === type.id
                            ? "border-primary-500 bg-primary-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div
                          className={`p-2.5 rounded-lg mr-4 ${order.delivery_type === type.id ? "bg-primary-100" : "bg-gray-100"}`}
                        >
                          <Icon
                            className={`w-5 h-5 ${order.delivery_type === type.id ? "text-primary-600" : "text-gray-400"}`}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p
                              className={`font-semibold ${order.delivery_type === type.id ? "text-primary-900" : "text-gray-900"}`}
                            >
                              {type.label}
                            </p>
                            <p className="font-bold text-lg">
                              EGP {type.price}
                            </p>
                          </div>
                          <p className="text-sm text-gray-500">{type.time}</p>
                        </div>
                        {order.delivery_type === type.id && (
                          <CheckCircle className="w-5 h-5 text-primary-500 ml-4" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Payment Method
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {paymentMethods.map((method) => {
                    const Icon = method.icon;
                    return (
                      <button
                        key={method.id}
                        onClick={() => updateField("payment_method", method.id)}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          order.payment_method === method.id
                            ? "border-primary-500 bg-primary-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <Icon
                          className={`w-6 h-6 mx-auto mb-2 ${order.payment_method === method.id ? "text-primary-600" : "text-gray-400"}`}
                        />
                        <p
                          className={`font-medium text-sm ${order.payment_method === method.id ? "text-primary-900" : "text-gray-900"}`}
                        >
                          {method.label}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Live Price Calculation */}
              {calculatedPrice && (
                <div className="p-4 bg-primary-50 rounded-xl border border-primary-200">
                  <h4 className="font-semibold text-primary-900 mb-2">
                    Price Breakdown
                  </h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Distance</span>
                      <span className="font-medium">
                        {calculatedPrice.distance_km} km
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Base Fee</span>
                      <span className="font-medium">
                        EGP {formatMoney(calculatedPrice.base_fee)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Distance Fee</span>
                      <span className="font-medium">
                        EGP {formatMoney(calculatedPrice.distance_fee)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Weight Charge</span>
                      <span className="font-medium">
                        EGP {formatMoney(calculatedPrice.weight_fee)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">
                        EGP {formatMoney(calculatedPrice.subtotal)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax (14%)</span>
                      <span className="font-medium">
                        EGP {formatMoney(calculatedPrice.tax)}
                      </span>
                    </div>
                    <div className="flex justify-between text-base font-bold pt-2 border-t border-primary-200">
                      <span className="text-primary-900">Total</span>
                      <span className="text-primary-600">
                        EGP {formatMoney(calculatedPrice.total)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Confirm */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2 text-emerald-500" />
              Order Summary
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-xl space-y-3">
                <div className="flex items-start space-x-3">
                  <Home className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">
                      Pickup
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {order.pickup_address}
                    </p>
                    <p className="text-xs text-gray-500">
                      {getCityName(order.pickup_city_id)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">
                      Delivery
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {order.delivery_address}
                    </p>
                    <p className="text-xs text-gray-500">
                      {getCityName(order.delivery_city_id)}
                    </p>
                    <p className="text-sm text-gray-600 mt-0.5">
                      {order.recipient_name} • {order.recipient_phone}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Package</p>
                  <p className="font-medium text-gray-900">
                    {
                      packageTypes.find((p) => p.id === order.package_type)
                        ?.label
                    }
                  </p>
                  <p className="text-sm text-gray-600">
                    {order.weight}kg{" "}
                    {order.dimensions && `• ${order.dimensions}`}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Delivery</p>
                  <p className="font-medium text-gray-900">
                    {
                      deliveryTypes.find((d) => d.id === order.delivery_type)
                        ?.label
                    }
                  </p>
                </div>
              </div>

              {order.notes && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-xs text-amber-700 uppercase font-medium">
                    Notes
                  </p>
                  <p className="text-sm text-amber-900 mt-1">{order.notes}</p>
                </div>
              )}

              <div className="border-t border-gray-200 pt-4 space-y-2">
                {calculatedPrice ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Base Fee</span>
                      <span className="font-medium">
                        EGP {formatMoney(calculatedPrice.base_fee)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Distance Fee</span>
                      <span className="font-medium">
                        EGP {formatMoney(calculatedPrice.distance_fee)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Weight Charge</span>
                      <span className="font-medium">
                        EGP {formatMoney(calculatedPrice.weight_fee)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Tax (14%)</span>
                      <span className="font-medium">
                        EGP {formatMoney(calculatedPrice.tax)}
                      </span>
                    </div>
                    <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200">
                      <span>Total</span>
                      <span className="text-primary-600">
                        EGP {formatMoney(calculatedPrice.total)}
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-500">Calculating price...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        <button
          onClick={prevStep}
          disabled={step === 0}
          className="btn-secondary disabled:opacity-50 flex items-center"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        {step < steps.length - 1 ? (
          <button onClick={nextStep} className="btn-primary flex items-center">
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="btn-primary disabled:opacity-50 flex items-center"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Place Order (EGP{" "}
                {calculatedPrice ? formatMoney(calculatedPrice.total) : "0.00"})
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomerNewOrder;
